/**
 * The support-thread wire protocol (PLAN-NEXT H2, D-N2).
 *
 * Shared by the Worker (which writes these responses), the settings section
 * (which is the user's half) and the ops console (which is the operator's),
 * exactly like `sync/protocol.ts`, `share/protocol.ts` and `ai/protocol.ts`. One
 * module so three readers cannot drift — the console and the settings page
 * render the same categories and the same status words, and they do it because
 * they import them rather than because somebody kept two lists in step.
 *
 * ```
 * session  GET  /api/support                          → { ok, tickets[] }   my threads, newest activity first
 * session  POST /api/support                          → { ok, ticket }      open one (JSON, or multipart with images)
 * session  GET  /api/support/:id                      → { ok, ticket }      read the thread
 * session  POST /api/support/:id/reply                → { ok, ticket }      say something (JSON, or multipart)
 * session  POST /api/support/:id/close                → { ok, ticket }      "never mind"
 * session  GET  /api/support/:id/attachments/:aid      the image bytes, or 404
 * ```
 *
 * Every mutation answers with **the whole thread**, not with the message it
 * appended. It is one indexed read on a conversation of a dozen rows, and it
 * means the screen after a reply is the server's state rather than the client's
 * guess at it — the same reason the share dialog re-reads its list.
 *
 * ## Screenshots ride on the same two routes
 *
 * Opening a ticket and replying accept `multipart/form-data` as well as JSON:
 * the same text fields, plus up to {@link SUPPORT_MAX_IMAGES_PER_MESSAGE} parts
 * named `image`. There is no separate upload endpoint and no two-phase "get a
 * URL, then commit" dance, because a screenshot has no life of its own — it is
 * part of the message, it is written in the same D1 batch as the message, and an
 * upload that succeeded while its message failed would be an orphan nothing
 * points at. The caps and the sniff are below; `worker/support.ts` enforces
 * every one of them and answers stable codes for each.
 *
 * ## Email is a doorbell
 *
 * D-N2: Resend notifies, it does not converse. Nothing in this protocol carries
 * an email address, a message id or a delivery state, because the thread is the
 * single record and there is no inbound path to reconcile with it. What the
 * notification carries is stated in `worker/email.ts` and it is deliberately
 * less than it could be: no user-typed text of any kind, in either direction.
 */

/** Where the user's own threads live. Session-guarded, every statement scoped to them. */
export const SUPPORT_BASE_PATH = '/api/support';

/**
 * The page a notification links to.
 *
 * A route of its own since PLAN-SUPPORT, and specifically the *conversations
 * tab* of it: support outgrew being a section of Settings when it gained a
 * public half, and a notification about a reply has to land on the thread rather
 * than on the board beside it.
 *
 * It was `/settings#support` under H2, and that fragment still works — the
 * settings page turns it into a replace-navigate here, so every reply
 * notification ever sent still opens the conversation it was about.
 *
 * Three callers read this constant rather than writing the path: the publish
 * dialog, the gallery's footer and the Worker's reply email. They moved with it
 * for free, which is why it is a constant.
 */
export const SUPPORT_PAGE_PATH = '/support/conversations';

/**
 * The `id` the moved section still carries.
 *
 * Nothing links to it any more — {@link SUPPORT_PAGE_PATH} is a route now — and
 * it is kept because the legacy `/settings#support` fragment is what the
 * redirect matches on, and because a section that renders on a page is allowed
 * to be addressable.
 */
export const SUPPORT_SECTION_ID = 'support';

/** Where the console's queue lives in the SPA. Linked from the admin notification. */
export const SUPPORT_CONSOLE_PATH = '/admin/support';

/**
 * What a ticket can be about.
 *
 * Four, from H2, and they are the four an answer is *routed* by rather than a
 * taxonomy: a bug goes to whoever is holding the debugger, a feature request goes
 * on the list that feeds this plan's successor, billing is a Paddle question, and
 * `other` is the escape hatch that stops somebody agonising over which of the
 * first three their question is.
 *
 * Validated in code rather than by a CHECK constraint — see
 * `migrations/0013_support.sql` for why, and 0002/0003 for the precedent.
 */
export const SUPPORT_CATEGORIES = ['bug', 'feature', 'billing', 'other'] as const;

export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

/** Whether a value is a category this build offers. */
export function isSupportCategory(value: unknown): value is SupportCategory {
  return typeof value === 'string' && (SUPPORT_CATEGORIES as readonly string[]).includes(value);
}

/** How each category is named to a human, in both UIs. */
export const SUPPORT_CATEGORY_LABELS: Readonly<Record<SupportCategory, string>> = {
  bug: 'Something is broken',
  feature: 'Feature request',
  billing: 'Billing',
  other: 'Something else',
};

/** The console's shorter labels, for a table cell rather than a select. */
export const SUPPORT_CATEGORY_SHORT_LABELS: Readonly<Record<SupportCategory, string>> = {
  bug: 'Bug',
  feature: 'Feature',
  billing: 'Billing',
  other: 'Other',
};

/**
 * Where a conversation stands.
 *
 * - `open` — the last word is the user's; it is our turn.
 * - `answered` — the last word is ours; it is theirs.
 * - `closed` — nobody's turn.
 *
 * The transitions live in `worker/support.ts` and nowhere else.
 */
export const SUPPORT_STATUSES = ['open', 'answered', 'closed'] as const;

export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

/** Whether a value is one of the three statuses. Used by the console's filter. */
export function isSupportStatus(value: unknown): value is SupportStatus {
  return typeof value === 'string' && (SUPPORT_STATUSES as readonly string[]).includes(value);
}

/**
 * Conversations one account may have going at once.
 *
 * Five, and it is a flood guard rather than a policy: nobody with five live
 * questions needs a sixth thread more than they need an answer on one of the
 * five. Counted over `open` **and** `answered` — an answered ticket is still a
 * live conversation — and never over `closed`, so the cap clears itself as
 * threads finish.
 *
 * The cap is checked when a ticket is *created* and never when one is replied to.
 * A reply is a continuation of a conversation the operator already knows about,
 * and refusing it would push somebody into opening a duplicate thread, which is
 * the exact outcome the cap exists to prevent.
 */
export const MAX_OPEN_TICKETS_PER_USER = 5;

/**
 * How long a subject may be.
 *
 * A line, not a paragraph. It is rendered in a queue row and in a list row, and
 * anything that does not fit on one line belongs in the message.
 */
export const SUPPORT_SUBJECT_MAX_LENGTH = 120;

/**
 * How long one message may be.
 *
 * Generous — a good bug report with steps, versions and what was expected is
 * longer than people assume — and bounded because this is a text column somebody
 * can POST to.
 */
export const SUPPORT_BODY_MAX_LENGTH = 4_000;

/**
 * Turns one thread may take before the user has to open a fresh one.
 *
 * Enforced on the *user's* messages only. The operator is not the flood risk on
 * a thread the operator is answering, and a support reply that fails because a
 * counter ran out is a worse failure than a long thread.
 */
export const SUPPORT_MAX_MESSAGES_PER_TICKET = 200;

// ---------------------------------------------------------------------------
// Screenshots
// ---------------------------------------------------------------------------

/**
 * The hard cap on one attached image, in bytes.
 *
 * 8 MB, because that is what the thing people actually attach weighs: a
 * full-screen screenshot on a retina display is commonly 2–8 MB of PNG, and a
 * cap under that would refuse the *normal* case and teach everybody to crop
 * before they ask for help. Nothing resizes on the way in — a screenshot that
 * has been re-encoded to fit a bound is a screenshot whose text has stopped
 * being readable, which is the one property it was sent for.
 *
 * Measured from the body bytes after they are read, never from `Content-Length`
 * — that header is the uploader's claim about the body, and this is the body.
 * `worker/profile.ts` makes the same distinction for avatars.
 */
export const SUPPORT_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

/**
 * How many images may ride on one message.
 *
 * Three, which covers "the screen, the error, and the thing I was doing before
 * it" and stops well short of somebody pasting a folder. It is not a storage
 * bound — {@link SUPPORT_MAX_ATTACHMENT_BYTES_PER_USER} is — it is a bound on
 * how much one turn of a conversation can be. A fourth image belongs in a
 * follow-up reply, where it arrives with a sentence saying what it shows.
 */
export const SUPPORT_MAX_IMAGES_PER_MESSAGE = 3;

/**
 * How many bytes of attachments one account may have stored, in total.
 *
 * 50 MB, summed over every attachment on every ticket the account owns — open,
 * answered and closed alike — and checked at write time as one indexed `SUM`.
 *
 * **This is the real abuse guard**, and the per-message caps above are not.
 * Closed tickets do not free their storage, so anything scoped to a live thread
 * is a bound somebody clears by closing threads and opening new ones; a
 * per-account byte quota is the only shape that actually bounds what a single
 * account can cost. Roughly a dozen full-size screenshots at the top of
 * {@link SUPPORT_IMAGE_MAX_BYTES}, or hundreds of ordinary ones — which is far
 * more support than anybody has, and is meant to be.
 *
 * Reaching it refuses the *attachment*, never the message: the conversation
 * still sends without the image, because somebody who cannot attach a
 * screenshot must still be able to say what is wrong.
 */
export const SUPPORT_MAX_ATTACHMENT_BYTES_PER_USER = 50 * 1024 * 1024;

/**
 * What may be attached, and it is a short list on purpose.
 *
 * The three formats a screenshot is ever in: PNG (every operating system's
 * screenshot key), JPEG (a photograph of a screen, or a phone's share sheet) and
 * WebP (what a browser hands over when you drag an image out of a page). No GIF,
 * no PDF, and no SVG — SVG is the one worth naming, because it is a document
 * with script in it, and these are served back from the app's own origin.
 *
 * The declared `Content-Type` of an uploaded part is never consulted. The
 * **magic bytes** decide; see {@link sniffSupportImageType}.
 */
export type SupportImageContentType = 'image/png' | 'image/jpeg' | 'image/webp';

/** The accept list a file picker offers, in the order the sniff tries them. */
export const SUPPORT_IMAGE_CONTENT_TYPES: readonly SupportImageContentType[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
];

/** How long a browser may hold an attachment it has been served, in seconds. */
export const SUPPORT_ATTACHMENT_CACHE_SECONDS = 300;

/**
 * The content type of these bytes, from their leading bytes, or `null`.
 *
 * The *only* thing that decides what an attachment is, exactly as
 * `sniffAvatarType` is for avatars — and it is the same function with JPEG added,
 * deliberately not shared with it: the two lists differ (an avatar may not be a
 * JPEG, because the client encodes WebP and falls back to PNG) and merging them
 * would mean one feature's widening silently widened the other's.
 *
 * - PNG: the eight-byte signature `89 50 4E 47 0D 0A 1A 0A`.
 * - JPEG: `FF D8 FF`. The fourth byte names the marker segment (`E0` for JFIF,
 *   `E1` for Exif, `DB` for a raw quantisation table) and is not checked —
 *   decoders disagree about which of those they emit, and every one of them is a
 *   JPEG.
 * - WebP: `RIFF` at 0, `WEBP` at 8. The four bytes between are the file length,
 *   which is not checked: a lying length makes a corrupt image, not an unsafe
 *   one, and `<img>` simply fails to draw it.
 */
export function sniffSupportImageType(bytes: Uint8Array): SupportImageContentType | null {
  if (bytes.length >= 8 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)) {
    return 'image/png';
  }
  if (bytes.length >= 3 && JPEG_SIGNATURE.every((byte, index) => bytes[index] === byte)) {
    return 'image/jpeg';
  }
  if (bytes.length >= 12 && matchesAscii(bytes, 0, 'RIFF') && matchesAscii(bytes, 8, 'WEBP')) {
    return 'image/webp';
  }
  return null;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff] as const;

function matchesAscii(bytes: Uint8Array, offset: number, ascii: string): boolean {
  for (let index = 0; index < ascii.length; index += 1) {
    if (bytes[offset + index] !== ascii.charCodeAt(index)) return false;
  }
  return true;
}

/**
 * One image on one message, as both thread views render it.
 *
 * No file name and no R2 key. The key is a server detail, and the name is the
 * uploader's string — it would be drawn on a page in the ops console and in the
 * user's own settings, which is a stored-text surface for a fact nobody needs:
 * what an attachment *is* is the picture, and `alt` text that says "Attachment 2"
 * is honest where a name is merely echoed. The size rides along so a client can
 * say how big it is without fetching it.
 */
export interface SupportAttachment {
  readonly id: number;
  /** The sniffed type, as stored. Never what the uploader declared. */
  readonly contentType: string;
  readonly bytes: number;
}

/**
 * The images on a message, tolerating a payload that has none.
 *
 * Used by both thread views instead of reading the field directly, for the
 * reason `parseProfile` exists: a browser holding a cached bundle from after
 * this feature has to read a Worker from before it, and the field would simply
 * be absent. An empty array is the same shape as "nothing attached", which every
 * renderer already handles, so the degradation costs a line and no branch.
 */
export function attachmentsOf(message: SupportMessage): readonly SupportAttachment[] {
  return Array.isArray(message.attachments) ? message.attachments : [];
}

/** One turn of a conversation. */
export interface SupportMessage {
  readonly id: number;
  /** Who said it. `admin` is "Beerwright", never a named operator — see the migration. */
  readonly author: 'user' | 'admin';
  readonly body: string;
  /** Seconds since the epoch, as stored. */
  readonly createdAt: number;
  /** The images attached to this turn, oldest first. Empty when there are none. */
  readonly attachments: readonly SupportAttachment[];
}

/** One thread in the user's list: the ticket, plus enough to decide whether to open it. */
export interface SupportTicketSummary {
  readonly id: string;
  readonly category: SupportCategory | string;
  readonly subject: string;
  readonly status: SupportStatus | string;
  readonly createdAt: number;
  /** Last activity, which is what the list is sorted by. */
  readonly updatedAt: number;
  readonly messageCount: number;
  /** The most recent turn, so the list can show who spoke last and when. */
  readonly lastMessage: SupportMessage | null;
}

/** A thread, with every message in it. */
export interface SupportThread extends SupportTicketSummary {
  readonly messages: readonly SupportMessage[];
}

/** `GET /api/support`. */
export interface SupportTicketsResponse {
  readonly ok: true;
  readonly tickets: readonly SupportTicketSummary[];
}

/** Every route that returns one thread: create, read, reply, close. */
export interface SupportThreadResponse {
  readonly ok: true;
  readonly ticket: SupportThread;
}

/** A refusal. `error` is a stable code, never prose. */
export interface SupportErrorResponse {
  readonly ok: false;
  readonly error: string;
}

/**
 * Whether it is the user's turn to read something.
 *
 * The **unread-ish** signal H2 asks for, and it is `status === 'answered'`
 * rather than a read flag, because a read flag would need a read receipt, a
 * per-device notion of "read", and a write on every thread open — three moving
 * parts to learn something the status already says: the last word is ours.
 *
 * What it costs is that a thread stays flagged after the user has read it and
 * said nothing. That is the honest state of a conversation somebody has been
 * answered in and not replied to, and closing it (or replying) clears it.
 */
export function awaitingUser(ticket: SupportTicketSummary): boolean {
  return ticket.status === 'answered';
}

/** One thread: `GET` reads it. */
export function supportTicketPath(ticketId: string): string {
  return `${SUPPORT_BASE_PATH}/${encodeURIComponent(ticketId)}`;
}

/** Say something on a thread. */
export function supportReplyPath(ticketId: string): string {
  return `${supportTicketPath(ticketId)}/reply`;
}

/** Close a thread from the user's side. */
export function supportClosePath(ticketId: string): string {
  return `${supportTicketPath(ticketId)}/close`;
}

/**
 * One attachment's bytes, for the account that owns the thread.
 *
 * Nested under the ticket rather than given a prefix of its own, because unlike
 * an avatar this is **private**: the id in the path is a name, and the
 * authorization is the same `WHERE user_id = ?` every other route here uses. A
 * URL shape that put attachments beside `/api/avatars` would invite the same
 * public treatment, and these are screenshots of somebody's screen.
 */
export function supportAttachmentPath(ticketId: string, attachmentId: number): string {
  return `${supportTicketPath(ticketId)}/attachments/${attachmentId}`;
}

/**
 * The same bytes, for an operator.
 *
 * `/api/admin/...` written out, as every path in `admin/client.ts` is — the
 * console's base is module-private in `worker/index.ts` for a runtime reason
 * stated there, so the console's URLs are literals wherever they are built. It
 * is the same object and the same helper behind it (`serveSupportAttachment`);
 * what differs is that an operator's route is not scoped to an owner, which is
 * the job.
 */
export function adminSupportAttachmentPath(ticketId: string, attachmentId: number): string {
  return `/api/admin/support/${encodeURIComponent(ticketId)}/attachments/${attachmentId}`;
}
